import { Mesh } from 'ecs/components/mesh';
import { Transform } from 'ecs/components/transform';
import { Context, TIME_STEP } from 'engine/context';
import { System, Target } from 'engine/ecs';
import { Entity } from 'engine/ecs';
import { mat4 } from 'wgpu-matrix';

export class RenderSystem extends System {
    target = Target.Render;
    componentsRequired = new Set<Function>([Mesh, Transform]);

    constructor(ctx: Context) {
        super();
    }

    update(entities: Set<Entity>, ctx: Context): void {
        const canvasTexture = ctx.context.getCurrentTexture();
        const renderPassDescriptor: GPURenderPassDescriptor = {
            colorAttachments: [
                {
                    view: ctx.multisampleTexture.createView(),
                    resolveTarget: canvasTexture.createView(),
                    clearValue: [0, 0, 0, 1],
                    loadOp: 'clear',
                    storeOp: 'store',
                } as GPURenderPassColorAttachment,
            ],
            depthStencilAttachment: {
                view: ctx.depthTexture.createView(),
                depthClearValue: 1.0,
                depthLoadOp: 'clear',
                depthStoreOp: 'store',
            },
        };

        const commandEncoder = ctx.device.createCommandEncoder();
        const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);

        passEncoder.setPipeline(ctx.modelPipeline.pipeline);

        for (const entity of entities) {
            const components = this.ecs.getComponents(entity);
            const mesh = components.get(Mesh);
            const transform = components.get(Transform);

            for (const primitive of mesh.primitives) {
                ctx.modelPipeline.updateUniforms(ctx, primitive.uniformBuffer, {
                    hasTexture: primitive.baseColor.label === 'empty' ? 0 : 1,
                    viewMatrix: mat4.lookAt(ctx.camera.eye, ctx.camera.target, [0, 1, 0]),
                    projectionMatrix: mat4.perspective((2 * Math.PI) / 5, ctx.aspect, 0.1, 100.0),
                    modelMatrix: transform.getMatrix(ctx.getFrameAlpha())
                });

                passEncoder.setBindGroup(0, primitive.uniformBindGroup);
                passEncoder.setVertexBuffer(0, primitive.positionBuffer);
                passEncoder.setVertexBuffer(1, primitive.normalBuffer);
                passEncoder.setVertexBuffer(2, primitive.texCoordBuffer);
                passEncoder.setIndexBuffer(primitive.indexBuffer, 'uint16');
                passEncoder.drawIndexed(primitive.length);
            }
        }

        passEncoder.end();
        ctx.device.queue.submit([commandEncoder.finish()]);
    }
}
